import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { BREEDS } from '../data/mock';
import { studService } from '../services/studApi';
import { X, Trash2, Loader2, AlertTriangle, Plus } from 'lucide-react';

export const StudForm = ({ isOpen, onClose, onSave, onDelete, editingStud }) => {
  const [formData, setFormData] = useState({
    breed: '',
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Filter breeds - exclude adoption and "otras razas"
  const breedOptions = BREEDS.filter(b => b.id !== 'adopcion' && b.id !== 'otras-razas');

  useEffect(() => {
    if (editingStud) {
      setFormData({
        breed: editingStud.breed || '',
        images: editingStud.images || []
      });
    } else {
      setFormData({
        breed: '',
        images: []
      });
    }
    setShowDeleteConfirm(false);
    setDeleting(false);
  }, [editingStud, isOpen]);

  // Compress image before upload - more aggressive
  const compressImage = (file, maxWidth = 800, quality = 0.5) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (formData.images.length >= 4) {
      alert('Máximo 4 fotos');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('La imagen es muy grande. Máximo 50MB.');
      return;
    }

    try {
      setUploading(true);
      
      // Compress image before upload
      const compressedFile = await compressImage(file, 800, 0.5);
      const imageUrl = await studService.uploadImage(compressedFile);
      setFormData({ 
        ...formData, 
        images: [...formData.images, imageUrl] 
      });
    } catch (err) {
      void err;
      alert('Error al subir la imagen. Intenta con una imagen más pequeña.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: editingStud?.id || Date.now().toString()
    });
    onClose();
  };

  const handleDelete = async () => {
    if (!editingStud) return;
    
    setDeleting(true);
    try {
      await onDelete(editingStud.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      void err;
      alert('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-purple-900">
            {editingStud ? 'Editar' : 'Añadir Nuevo'}
          </DialogTitle>
        </DialogHeader>
        
        {editingStud && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center justify-center gap-2 bg-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-600 active:bg-red-700"
              >
                <Trash2 className="w-5 h-5" />
                Eliminar
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">¿Eliminar?</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                    disabled={deleting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Sí, eliminar'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Raza */}
          <div className="space-y-2">
            <Label className="text-purple-800 font-semibold">Raza</Label>
            <Select value={formData.breed} onValueChange={(value) => setFormData({...formData, breed: value})}>
              <SelectTrigger className="border-purple-200">
                <SelectValue placeholder="Selecciona raza" />
              </SelectTrigger>
              <SelectContent>
                {breedOptions.map((breed) => (
                  <SelectItem key={breed.id} value={breed.id}>
                    {breed.icon} {breed.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fotos */}
          <div className="space-y-2">
            <Label className="text-purple-800 font-semibold">Fotos (máx. 4)</Label>
            <div className="grid grid-cols-2 gap-3">
              {formData.images.map((img, index) => (
                <div key={`img-${img.slice(-20)}-${index}`} className="relative">
                  <img 
                    src={img} 
                    alt={`Foto ${index + 1}`} 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {formData.images.length < 4 && (
                <label className="border-2 border-dashed border-purple-200 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 transition-colors">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-8 h-8 text-purple-400" />
                      <span className="text-purple-600 text-sm mt-1">Añadir foto</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-purple-500">{formData.images.length}/4 fotos</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white flex-1" disabled={uploading}>
              {editingStud ? 'Guardar' : 'Añadir'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
